import {
  documentCategoriesCreateMutation,
  documentCategoriesDestroyMutation,
  documentCategoriesListOptions,
  documentCategoriesListQueryKey,
  documentCategoriesPartialUpdateMutation,
  documentCategoriesRetrieveOptions,
  DocumentCategoryReadable
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import {
  CatalogActions,
  CatalogAddButton,
  FormModal,
  FormNomenclatureDefaultItem
} from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import { DayLabel, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App } from 'antd'
import { Edit, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'documents'

export const NomenclaturesDocuments = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const sorter = useTableSorter()
  const { resetPagination } = useResetPagination()

  const { notification } = App.useApp()

  const [isCreate, setIsCreate] = useState(false)
  const [editItemId, setEditItemId] = useState<number>()
  const [deleteItemId, setDeleteItemId] = useState<number>()

  const { data: documents, isLoading } = useQuery(
    documentCategoriesListOptions({
      query: {
        ...defaultFilters,
        ...params
      }
    })
  )

  const { mutate } = useMutation({
    ...documentCategoriesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([documentCategoriesListQueryKey()])
      resetPagination()
      setDeleteItemId(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onDelete = () => {
    if (deleteItemId) {
      mutate({ path: { id: deleteItemId } })
    }
  }

  const onCancelDelete = () => setDeleteItemId(undefined)

  const onCloseModal = () => {
    setEditItemId(undefined)
    setIsCreate(false)
  }

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        key: 'edit',
        icon: <Edit size={20} />,
        onClick: () => setEditItemId(id)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        key: 'delete',
        icon: <Trash size={20} />,
        danger: true,
        onClick: () => setDeleteItemId(id)
      }
    ],
    [t]
  )

  const columns: TableColumnsType<DocumentCategoryReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <CatalogActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <>
      <TableContainer
        data={documents}
        columns={columns}
        loading={isLoading}
        rightChildren={
          <CatalogAddButton onClick={() => setIsCreate(true)}>
            {t('nomenclature.add_category')}
          </CatalogAddButton>
        }
        onChange={(_, __, result) => sorter(result)}
        bordered
      />

      <ConfirmModal
        open={!!deleteItemId}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />
      <FormModal
        id={editItemId}
        formName={formName}
        open={!!(editItemId || isCreate)}
        getItem={documentCategoriesRetrieveOptions}
        onCreate={documentCategoriesCreateMutation}
        onUpdate={documentCategoriesPartialUpdateMutation}
        onCloseModal={onCloseModal}
      >
        <FormNomenclatureDefaultItem />
      </FormModal>
    </>
  )
}
